/**
 * Financials tab — the three statements plus computed ratio analysis.
 *
 * The analyst reads the data room's own statements here: a profit & loss, a balance sheet and a
 * cash flow, each across every fiscal year the document reports, in the document's own line
 * order. Every ratio underneath is COMPUTED from those lines (@krit/core/financials) rather than
 * extracted, so a number on screen can always be traced to the statement rows above it.
 *
 * Until the data-room file lands, the statements fall back to what the deck states (a revenue /
 * EBITDA / PAT series) and the tab says so — an honest thin P&L beats invented detail.
 */
import { Fragment, useMemo, useState, type ReactNode } from 'react';
import {
  commonSize, computeRatios, dealStatements, finSeries, fmtFin, fmtRatio, isMoneyLine,
  isProjectedYear, latestActualIdx, proj, inr, pct, statementHasContent,
  type FinLine, type FinStatement, type FinancialStatements, type RatioAnalysis, type RatioRow,
} from '@krit/core';
import { financialsExtract, uploadDocument, ocrDeckImages, type DealDto, type FinancialsExtractResult } from '../lib/api.js';
import { extractDeckText, renderPdfToImages, isLikelyScanned } from '../lib/pdf.js';
import { extractFinancialsText } from '../lib/xlsx.js';
import { SectionInsight, useInsightRefresh } from '../lib/probe42Format.js';
import { Card, InlineBusy, Pill } from './ui.js';

/* ───────────────────────────── small shared bits ───────────────────────────── */

function DL({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[12.5px]">
      {rows.map(([k, v], i) => (
        <div key={i} className="contents"><dt className="text-muted font-semibold">{k}</dt><dd className="text-navy font-semibold text-right">{v}</dd></div>
      ))}
    </dl>
  );
}

function Empty({ label }: { label: string }) {
  return <div className="text-center py-6 text-muted text-[12.5px] leading-relaxed max-w-[520px] mx-auto">{label}</div>;
}

const TH_BASE = 'text-muted font-bold text-[10.5px] uppercase tracking-[0.4px] px-2 py-1.5 border-b border-line whitespace-nowrap';
// The label column stays put while the year columns scroll — an 8-year statement is wider than the
// card. Each use pairs it with an OPAQUE background of its own; a translucent one lets the year
// figures show through as they scroll underneath.
const STICKY = 'sticky left-0 z-10';

/** Year header cells, shared by the statement and ratio tables (projections marked). */
function YearHead({ years, trailing }: { years: string[]; trailing?: string }) {
  return (
    <tr>
      <th className={`${STICKY} bg-card ${TH_BASE} text-left`}>Line item</th>
      {years.map((y, i) => (
        <th key={i} className={`${TH_BASE} text-right ${isProjectedYear(y) ? 'text-muted-2' : ''}`}>{y}</th>
      ))}
      {trailing && <th className={`${TH_BASE} text-right`}>{trailing}</th>}
    </tr>
  );
}

/* ───────────────────────────── statement table ───────────────────────────── */

function StmtTable({ years, lines, common, revenue }: {
  years: string[]; lines: FinLine[]; common: boolean; revenue: (number | null)[];
}) {
  return (
    <div className="overflow-x-auto -mx-5 px-5">
      <table className="w-full min-w-[560px] border-collapse text-[12px]">
        <thead><YearHead years={years} /></thead>
        <tbody>
          {lines.map((l, i) => {
            const asPct = common && isMoneyLine(l.label);
            const values = asPct ? commonSize(l, revenue) : l.values;
            return (
              <tr key={i} className={l.bold ? 'bg-canvas' : ''}>
                <td
                  className={`${STICKY} px-2 py-1.5 border-b border-line-2 ${l.bold ? 'bg-canvas text-navy font-semibold' : 'bg-card text-muted'}`}
                  style={{ paddingLeft: 8 + (l.indent ?? 0) * 14 }}
                >
                  {l.label}
                </td>
                {values.map((v, j) => (
                  <td key={j} className={`px-2 py-1.5 border-b border-line-2 text-right mono whitespace-nowrap ${v != null && v < 0 ? 'text-red' : l.bold ? 'text-navy font-semibold' : 'text-navy'}`}>
                    {asPct ? fmtRatio(v, '%') : fmtFin(v)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** One statement card (P&L / balance sheet / cash flow) with an optional common-size toggle. */
function StatementCard({ title, sub, st, years, revenue, emptyLabel, allowCommonSize = false }: {
  title: string; sub?: ReactNode; st: FinStatement; years: string[]; revenue: (number | null)[];
  emptyLabel: string; allowCommonSize?: boolean;
}) {
  const [common, setCommon] = useState(false);
  const has = statementHasContent(st);
  const canCommonSize = allowCommonSize && revenue.some((x) => x != null);
  return (
    <Card
      className="mb-4"
      title={title}
      sub={sub ?? (st.note || undefined)}
      actions={has && canCommonSize ? (
        <button
          type="button"
          onClick={() => setCommon((x) => !x)}
          className={`px-2.5 py-1 rounded-md text-[11.5px] font-semibold border ${common ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-line hover:bg-canvas'}`}
          title="Show each line as a % of that year's revenue"
        >
          % of revenue
        </button>
      ) : undefined}
    >
      {has ? <StmtTable years={years} lines={st.lines} common={common} revenue={revenue} /> : <Empty label={emptyLabel} />}
    </Card>
  );
}

/* ───────────────────────────── ratio analysis ───────────────────────────── */

/** Latest-vs-previous move for a ratio, read against whether "up" is good for that ratio. */
function trend(row: RatioRow, upto: number): { mark: string; tone: string } | null {
  let last = -1;
  for (let i = Math.min(upto, row.values.length - 1); i >= 0; i--) if (row.values[i] != null) { last = i; break; }
  if (last <= 0) return null;
  let prev = -1;
  for (let i = last - 1; i >= 0; i--) if (row.values[i] != null) { prev = i; break; }
  if (prev < 0) return null;
  const delta = row.values[last]! - row.values[prev]!;
  const scale = Math.max(1e-9, Math.abs(row.values[prev]!));
  if (Math.abs(delta) / scale < 0.02) return { mark: '→', tone: 'text-muted-2' }; // inside 2% = flat
  const better = row.dir == null ? null : (delta > 0) === (row.dir === 'up');
  return { mark: delta > 0 ? '▲' : '▼', tone: better == null ? 'text-muted-2' : better ? 'text-green' : 'text-red' };
}

function RatioTable({ r }: { r: RatioAnalysis }) {
  const iLast = latestActualIdx(r.years);
  return (
    <div className="overflow-x-auto -mx-5 px-5">
      <table className="w-full min-w-[560px] border-collapse text-[12px]">
        <thead><YearHead years={r.years} trailing="Trend" /></thead>
        <tbody>
          {r.groups.map((g) => (
            <Fragment key={g.key}>
              <tr>
                <td colSpan={r.years.length + 2} className="px-2 pt-3.5 pb-1 text-[10.5px] font-bold uppercase tracking-[0.5px] text-teal-d">{g.label}</td>
              </tr>
              {g.rows.map((row) => {
                const t = trend(row, iLast);
                return (
                  <tr key={row.key} title={row.note}>
                    <td className={`${STICKY} bg-card px-2 py-1.5 border-b border-line-2 text-muted`}>{row.label}</td>
                    {row.values.map((v, i) => (
                      <td key={i} className={`px-2 py-1.5 border-b border-line-2 text-right mono whitespace-nowrap ${i === iLast ? 'text-navy font-semibold' : v != null && v < 0 ? 'text-red' : 'text-navy'}`}>
                        {fmtRatio(v, row.unit)}
                      </td>
                    ))}
                    <td className={`px-2 py-1.5 border-b border-line-2 text-right ${t?.tone ?? 'text-muted-2'}`}>{t?.mark ?? '—'}</td>
                  </tr>
                );
              })}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ───────────────────────────── headline summary ───────────────────────────── */

/** The two most recent ACTUAL years, for the at-a-glance comparison. */
function headlineYears(years: string[]): { cur: number; prev: number } | null {
  const actuals = years.map((y, i) => i).filter((i) => !isProjectedYear(years[i]!));
  const cur = actuals[actuals.length - 1];
  if (cur == null) return null;
  return { cur, prev: actuals[actuals.length - 2] ?? -1 };
}

function HeadlineCard({ fs, series }: { fs: FinancialStatements; series: Record<string, (number | null)[]> }) {
  const h = headlineYears(fs.years);
  if (!h) return null;
  const at = (k: string, i: number) => (i < 0 ? null : series[k]?.[i] ?? null);
  const yoy = (k: string) => {
    const a = at(k, h.cur), b = at(k, h.prev);
    return a == null || b == null || Math.abs(b) < 1e-9 ? null : (a / b - 1) * 100;
  };
  const margin = (k: string, i: number) => {
    const x = at(k, i), rev = at('revenue', i);
    return x == null || rev == null || Math.abs(rev) < 1e-9 ? null : (x / rev) * 100;
  };
  const curY = fs.years[h.cur]!, prevY = h.prev >= 0 ? fs.years[h.prev]! : null;
  const rows: [string, number | null, number | null, number | null, 'money' | 'pct'][] = [
    ['Revenue', at('revenue', h.prev), at('revenue', h.cur), yoy('revenue'), 'money'],
    ['EBITDA', at('ebitda', h.prev), at('ebitda', h.cur), yoy('ebitda'), 'money'],
    ['EBITDA margin', margin('ebitda', h.prev), margin('ebitda', h.cur), null, 'pct'],
    ['PAT', at('pat', h.prev), at('pat', h.cur), yoy('pat'), 'money'],
    ['PAT margin', margin('pat', h.prev), margin('pat', h.cur), null, 'pct'],
  ];
  const shown = rows.filter(([, a, b]) => a != null || b != null);
  if (!shown.length) return null;
  const fmt = (v: number | null, kind: 'money' | 'pct') => (kind === 'pct' ? fmtRatio(v, '%') : v == null ? '—' : inr(v));
  return (
    <Card className="mb-4" title={prevY ? `${prevY} → ${curY}` : curY} sub={`₹ Cr · ${fs.unit === '₹ Cr' ? 'actuals, margins, YoY' : fs.unit}`}>
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr>
            <th className={`${TH_BASE} text-left`}>Metric</th>
            {prevY && <th className={`${TH_BASE} text-right`}>{prevY}</th>}
            <th className={`${TH_BASE} text-right`}>{curY}</th>
            <th className={`${TH_BASE} text-right`}>YoY</th>
          </tr>
        </thead>
        <tbody>
          {shown.map(([label, a, b, g, kind]) => (
            <tr key={label}>
              <td className="px-2 py-1.5 border-b border-line-2 text-muted">{label}</td>
              {prevY && <td className="px-2 py-1.5 border-b border-line-2 text-right mono text-navy">{fmt(a, kind)}</td>}
              <td className="px-2 py-1.5 border-b border-line-2 text-right mono text-navy font-semibold">{fmt(b, kind)}</td>
              <td className={`px-2 py-1.5 border-b border-line-2 text-right mono ${g == null ? 'text-muted-2' : g >= 0 ? 'text-green' : 'text-red'}`}>{g == null ? '—' : fmtRatio(g, '%')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/* ───────────────────────────── the tab ───────────────────────────── */

/** A one-line human summary of what an extraction run produced. */
function extractSummary(r: FinancialsExtractResult): string {
  const s = r.statements;
  const parts: string[] = [];
  if (s) parts.push(`${s.years} year${s.years === 1 ? '' : 's'} · P&L ${s.pnl} lines · balance sheet ${s.balanceSheet} · cash flow ${s.cashFlow}`);
  if (r.filled.length) parts.push(`updated ${r.filled.filter((f) => f !== 'financials').join(', ') || 'the statements'}`);
  if (!parts.length) return `No figures found in ${r.source}.`;
  return `From ${r.source}: ${parts.join(' · ')}.`;
}

export function Financials({ d, dealId, onChanged }: { d: DealDto; dealId: string; onChanged: () => void }) {
  const [busy, setBusy] = useState<'' | 'file' | 'dataroom'>('');
  const [msg, setMsg] = useState('');
  const [warn, setWarn] = useState(false);
  const refreshInsight = useInsightRefresh(dealId);

  const { fs, derived } = useMemo(() => dealStatements(d), [d]);
  const series = useMemo(() => finSeries(fs), [fs]);
  const ratios = useMemo(() => computeRatios(fs), [fs]);
  const revenue = series.revenue ?? fs.years.map(() => null);
  const p = proj(d);

  /**
   * The analyst picks the data room's financials file → the browser turns it into text (PDF via
   * pdf.js, XLSX via the workbook parser, CSV as-is), stores the file on the deal, and the server
   * extracts the three statements from that text.
   */
  async function onFile(file: File) {
    setBusy('file'); setWarn(false); setMsg(`Reading ${file.name}…`);
    try {
      let text = await extractFinancialsText(file, extractDeckText);
      // Scanned financial PDFs (photographed/signed statements) have no text layer → pdf.js returns
      // nothing. Render the pages and OCR them with the vision model, same as the deck upload.
      const isPdf = file.name.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';
      if (isPdf && (text.trim().length < 40 || isLikelyScanned(text))) {
        setMsg('Scanned PDF — reading the pages with OCR (can take ~30s)…');
        try { const o = await ocrDeckImages(await renderPdfToImages(file)); if (o.text.trim()) text = o.text; } catch { /* fall through to the guard below */ }
      }
      if (text.trim().length < 40) throw new Error('Could not read any figures from this file, even with OCR. Export the sheet as XLSX, CSV or PDF and retry.');
      setMsg('Storing the file and extracting the statements with AI — this takes ~30s…');
      try { await uploadDocument(dealId, file, text, 0); } catch { /* store best-effort; extraction is what matters */ }
      const r = await financialsExtract(dealId, { text, source: file.name });
      setWarn(!r.statements);
      setMsg(extractSummary(r));
      onChanged();
      if (r.statements) refreshInsight(['financials', 'valuation']);
    } catch (e) { setWarn(true); setMsg(String((e as Error).message)); }
    finally { setBusy(''); }
  }

  /** Re-extract from what the data-room team already shared (stored documents; deck as fallback). */
  async function fromDataRoom() {
    setBusy('dataroom'); setWarn(false); setMsg('Reading the deal’s stored documents and extracting the statements — this takes ~30s…');
    try {
      const r = await financialsExtract(dealId);
      setWarn(!r.statements || r.fromDeck);
      setMsg(r.fromDeck
        ? `${extractSummary(r)} Read from the deck — a deck rarely states a balance sheet or cash flow, so ask the data-room team for the financials pack.`
        : extractSummary(r));
      onChanged();
      if (r.statements) refreshInsight(['financials', 'valuation']);
    } catch (e) { setWarn(true); setMsg(String((e as Error).message)); }
    finally { setBusy(''); }
  }

  const anyBusy = busy !== '';
  const src = d.financials;
  const audited = src?.audited?.some((x) => x === false);

  return (
    <>
      <Card
        className="mb-4"
        title="Statements from the data room"
        sub={src?.source
          ? <>Extracted from <span className="font-semibold text-navy">{src.source}</span>{src.extractedAt ? ` · ${new Date(src.extractedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''} · {src.years.length} year{src.years.length === 1 ? '' : 's'} · ₹ Cr</>
          : 'Upload the detailed P&L / balance sheet / cash flow (PDF, CSV or XLSX). Every ratio below is computed from the lines it fills — nothing is estimated.'}
      >
        <div className="flex flex-wrap items-center gap-3">
          <label className={`px-3.5 py-2 rounded-lg text-[12.5px] font-semibold border cursor-pointer ${anyBusy ? 'opacity-60 pointer-events-none' : ''} bg-navy text-white border-navy hover:bg-navy-2`}>
            {busy === 'file' ? <InlineBusy>Working…</InlineBusy> : '⬆ Upload financials (PDF / CSV / XLSX)'}
            <input
              type="file"
              accept=".pdf,.csv,.tsv,.txt,.xlsx,.xlsm,.pptx,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              disabled={anyBusy}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}
            />
          </label>
          {d.documents.length > 0 && (
            <button
              type="button"
              disabled={anyBusy}
              onClick={fromDataRoom}
              title="Re-read the financials from the documents already attached to this deal"
              className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-teal text-white hover:bg-teal-d disabled:opacity-50"
            >
              {busy === 'dataroom' ? <InlineBusy>Extracting…</InlineBusy> : '↻ Extract from data room'}
            </button>
          )}
          {msg && <span className={`text-[12px] ${warn ? 'text-amber' : 'text-muted'}`}>{msg}</span>}
        </div>
        {(src?.notes?.length || audited) && (
          <ul className="mt-3 space-y-1 text-[11.5px] text-muted">
            {audited && <li>• Not every year is audited — check the per-year basis before relying on the newest column.</li>}
            {(src?.notes ?? []).map((n, i) => <li key={i}>• {n}</li>)}
          </ul>
        )}
        {derived && (
          <div className="mt-3 text-[11.5px] text-amber font-semibold bg-amber-50 rounded-lg px-3 py-2">
            ⚠ No data-room financials on this deal yet — the statements below are the deck’s headline figures only, so the balance sheet, cash flow and every ratio that needs them are blank. Upload the financials pack (or hit “Extract from data room”) to fill them.
          </div>
        )}
      </Card>

      {fs.years.length > 0 && (
        <Card title="What the financials show">
          <SectionInsight dealId={dealId} section="financials" label="Financial analysis" />
        </Card>
      )}

      <HeadlineCard fs={fs} series={series} />

      <StatementCard
        title="Profit & loss"
        sub={`₹ Cr · ${fs.years.length} year${fs.years.length === 1 ? '' : 's'}${fs.pnl.note ? ` · ${fs.pnl.note}` : ''}`}
        st={fs.pnl}
        years={fs.years}
        revenue={revenue}
        allowCommonSize
        emptyLabel="No profit & loss lines yet — upload the data room’s P&L above."
      />
      <StatementCard
        title="Balance sheet"
        sub="₹ Cr · closing balances as printed"
        st={fs.balanceSheet}
        years={fs.years}
        revenue={revenue}
        emptyLabel="No balance sheet yet. A pitch deck almost never states one — upload the data room’s balance sheet to unlock the leverage, liquidity, return and working-capital ratios."
      />
      <StatementCard
        title="Cash flow"
        sub="₹ Cr · outflows in brackets"
        st={fs.cashFlow}
        years={fs.years}
        revenue={revenue}
        emptyLabel="No cash flow statement yet — upload it to see cash conversion (OCF / EBITDA, OCF / PAT) and free cash flow."
      />

      <Card
        className="mb-4"
        title="Ratio analysis"
        sub="Computed from the statements above — growth, profitability, returns, liquidity, leverage, working capital and cash conversion. Balance-sheet ratios use closing balances; hover a row for its formula."
      >
        {ratios.groups.length ? (
          <>
            {ratios.summary.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3.5">
                {ratios.summary.map((s) => (
                  <Pill key={s.key} tone="navy">{s.label} {fmtRatio(s.value, s.unit)}<span className="font-normal text-muted-2 ml-1">{s.span}</span></Pill>
                ))}
              </div>
            )}
            <RatioTable r={ratios} />
          </>
        ) : (
          <Empty label="No ratios yet — they need at least a P&L across two years. Upload the data-room financials above." />
        )}
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Quality & risk metrics" sub="Held on the deal — these feed the Krit Score and credit grade">
          {/* 0 means "not captured" on a deal record, so it shows as "—" rather than a real-looking zero. */}
          <DL rows={[
            ['Net D/E', d.de ? `${d.de.toFixed(2)}x` : '—'],
            ['OCF / PAT', d.ocfPat ? pct(d.ocfPat) : '—'],
            ['Working-capital days', d.wcDays ? String(Math.round(d.wcDays)) : '—'],
            ['Top-3 concentration', d.top3 ? pct(d.top3) : '—'],
            ['Promoter stake', d.promoterStake ? pct(d.promoterStake) : '—'],
          ]} />
        </Card>
        <Card title="5-year projection" sub="Illustrative — revenue grown from FY26 at held growth; EBITDA/PAT only where the deck states a margin to hold">
          {d.rev26 ? (
            <table className="w-full border-collapse text-[12px]">
              <thead><tr>{['Year', 'Rev', 'EBITDA', 'PAT'].map((h, i) => <th key={h} className={`${TH_BASE} ${i > 0 ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr></thead>
              <tbody>
                {p.map((x) => (
                  <tr key={x.y}>
                    <td className="px-2 py-1.5 border-b border-line-2 text-muted">{x.y}</td>
                    {[x.rev, x.eb, x.pat].map((v, i) => (
                      <td key={i} className="px-2 py-1.5 border-b border-line-2 text-right mono text-navy">{v ? inr(v) : '—'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <Empty label="No FY26 revenue on the deal, so there is nothing to grow a projection from." />}
        </Card>
      </div>
    </>
  );
}
